import { useEffect, useState, type ReactNode } from "react";

interface AppBridgeProviderProps {
  apiKey: string;
  host: string | null;
  children: ReactNode;
}

/**
 * App Bridge v4 is loaded via the CDN <script> tag in index.html (must be the
 * FIRST script, no async/defer/type=module, per Shopify's strict rule). It
 * exposes `window.shopify` once ready.
 *
 * This component waits for `window.shopify` to be available before rendering
 * children, so any useAppBridge() / shopify.idToken() calls succeed.
 *
 * In dev/local without an embedded iframe (`host` is null), we skip App
 * Bridge entirely and render children directly. The api/client falls back to
 * VITE_DEV_SESSION_TOKEN if set.
 */
export function AppBridgeProvider({ apiKey, host, children }: AppBridgeProviderProps) {
  const [ready, setReady] = useState(false);
  const skip = !host || !apiKey;

  useEffect(() => {
    if (skip) return;
    if (window.shopify) {
      setReady(true);
      return;
    }
    // App Bridge CDN script is in index.html. Poll for window.shopify which
    // becomes available once the script finishes initializing.
    const interval = setInterval(() => {
      if (window.shopify) {
        setReady(true);
        clearInterval(interval);
      }
    }, 50);
    const timeout = setTimeout(() => clearInterval(interval), 10_000);
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [skip]);

  if (skip) return <>{children}</>;
  if (!ready) return null;
  return <>{children}</>;
}
